// src/slice/auth/authSlice.js
//
// The customer session the portal reads: a token, the user it belongs to, and
// the one action that throws both away.
//
// ── WHY THIS IS SO SMALL ──
// It used to carry a whole phone sign-in: three thunks driving Firebase Phone
// Auth, an OTP screen's worth of state, and a web Firebase app initialised
// from a config block committed into the source — a config that named someone
// else's project. None of it was reachable: no screen dispatched sendOTP,
// verifyOTP or completeRegistration, and the admin login the portal actually
// uses lives in adminUserSlice against `s.adminUser`.
//
// Two consumers read this slice. App.js takes `token` and dispatches logout on
// a 401; checkoutSlice falls back to `auth.token`. That is the whole contract,
// so that is all this holds.

import { createSlice } from "@reduxjs/toolkit";

const TOKEN_KEY = "token";
const USER_KEY = "user";

/**
 * How long this token has left, in milliseconds. Negative once it has expired,
 * and zero for anything that is not a readable JWT.
 *
 * ── WHY MILLISECONDS AND NOT A BOOLEAN ──
 * A yes/no answer cannot tell "no token at all" from "token that died a minute
 * ago", and those want different handling: the first is a visitor, the second
 * is someone who was working and is about to be interrupted. Returning the
 * margin leaves that decision to the caller.
 *
 * `exp` is seconds since the epoch, per RFC 7519 — multiplying by 1000 is the
 * step that gets left out, and leaving it out makes every token look like it
 * expired in 1970.
 */
export function tokenLifeLeft(token) {
  if (typeof token !== "string" || token.length === 0) return 0;

  const body = token.split(".")[1];
  if (!body) return 0;

  try {
    // Base64URL, which atob does not accept: - and _ are the URL-safe
    // substitutions for + and /, and the padding is usually dropped.
    const normalised = body.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalised.padEnd(
      normalised.length + ((4 - (normalised.length % 4)) % 4),
      "="
    );
    const claims = JSON.parse(atob(padded));
    if (typeof claims.exp !== "number") return 0;
    return claims.exp * 1000 - Date.now();
  } catch {
    // Anything unparseable is treated as expired rather than thrown: a
    // malformed token in storage must not stop the portal from loading.
    return 0;
  }
}

const usable = (token) => tokenLifeLeft(token) > 0;

/** Whatever survived from the last visit, or nothing. */
function sessionFromStorage() {
  let token = null;
  let user = null;

  try {
    token = window.localStorage.getItem(TOKEN_KEY);
    if (!usable(token)) {
      // Dropped on load rather than on the first rejected request, so the
      // portal never renders a signed-in shell it is about to be thrown out
      // of.
      window.localStorage.removeItem(TOKEN_KEY);
      window.localStorage.removeItem(USER_KEY);
      return { token: null, user: null };
    }
    const stored = window.localStorage.getItem(USER_KEY);
    user = stored ? JSON.parse(stored) : null;
  } catch {
    // Private windows and blocked site data throw on access. A session that
    // cannot be read is the same as no session.
    return { token: null, user: null };
  }

  return { token, user };
}

const restored = sessionFromStorage();

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: restored.user,
    token: restored.token,
    loading: false,
    error: null,
    isSuccess: false,
  },
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isSuccess = false;
      try {
        window.localStorage.removeItem(TOKEN_KEY);
        window.localStorage.removeItem(USER_KEY);
      } catch {
        /* nothing to clear if storage is unavailable */
      }
    },
    clearAuthState: (state) => {
      state.error = null;
      state.isSuccess = false;
    },
  },
});

export const { logout, clearAuthState } = authSlice.actions;
export default authSlice.reducer;
